/**
 * ── Candidate notes ──────────────────────────────────────────────────────────
 *
 * One candidate's notes, read when somebody clicks 📝, and the write that adds one. Its own
 * module (taActionBoardApi is at its line budget), re-exported there so `import * as api` reaches
 * both.
 *
 * ⚠️ Reading used to be baked into the board payload (`role.notes`). It was one Lever call per
 * candidate in the pipeline — a third of the board's whole per-candidate traffic — so it moved
 * here (Theresa, Oct 2026). `role.notes` still exists and is now always empty, so every card
 * renders the same whether its board is fresh or came from an old cache.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import type { CandidateNote } from '../types/taActionBoard';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';
const BASE = `${PROXY_URL}/api/ta-action-board`;

/**
 * Add a note to a candidate's Lever profile. Returns the created note (to append to the open list).
 * Analytics carry NO candidate data — only the note length bucket.
 */
export async function addCandidateNote(input: {
  opportunityId: string;
  boardEmail: string;
  value: string;
}): Promise<CandidateNote> {
  try {
    const res = await authenticatedFetch(`${BASE}/candidate-notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const data = (await res.json()) as { note: CandidateNote };
    void logAnalyticsEvent('candidate_note_added', { length_bucket: lengthBucket(input.value) });
    return data.note;
  } catch (error) {
    void logAnalyticsEvent('candidate_note_added_failed', {
      kind: error instanceof Error ? error.name : typeof error,
    });
    throw error;
  }
}

/** Coarse size bucket for analytics — never the note text itself. */
function lengthBucket(value: string): string {
  const n = value.trim().length;
  if (n <= 80) return 'short';
  if (n <= 400) return 'medium';
  return 'long';
}

/**
 * The notes on one candidate's Lever profile.
 *
 * Two failure flavours rather than one, because "nobody has written anything" is a CLAIM and the
 * modal makes it in words:
 *
 *  · `notesForbidden` — the Lever key has lost /notes access. Fixable, and worth naming.
 *  · `unread` — this candidate's own read failed (a throttle, a 5xx, a profile Lever would not
 *    name). The server answers 200 because the other candidates in a batch succeeded, so without
 *    this the modal would render an empty list and call it "no notes yet".
 *
 * Anything that fails outright throws, and the modal shows that too.
 */
export async function fetchCandidateNotes(opportunityId: string): Promise<{
  notes: CandidateNote[];
  notesForbidden: boolean;
  unread: boolean;
}> {
  const res = await authenticatedFetch(`${BASE}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ opportunityIds: [opportunityId] }),
  });
  const data = (await res.json()) as {
    notes?: Record<string, CandidateNote[]>;
    notesForbidden?: boolean;
    unreadable?: string[];
  };
  return {
    notes: data.notes?.[opportunityId] ?? [],
    notesForbidden: Boolean(data.notesForbidden),
    unread: (data.unreadable ?? []).includes(opportunityId),
  };
}
